import React from "react";
import { motion } from "framer-motion";
import { Users, MessageCircle, Calendar, BookOpen, HeartHandshake } from "lucide-react";
import BrandHeader from "@/components/BrandHeader";
import BrandFooter from "@/components/BrandFooter";
import EmailSignup from "@/components/EmailSignup";

const IMG = "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=1800&q=80";
const EASE = [0.16, 1, 0.3, 1];

const perks = [
  { Icon: BookOpen, title: "Members-only guides", body: "Deeper workbooks, printables, and monthly tools that never go public." },
  { Icon: MessageCircle, title: "A quiet community", body: "A calm, encouraging space — the opposite of your social feed." },
  { Icon: Calendar, title: "Live sessions", body: "Monthly live gatherings on wellness, discipline, and the seven pillars." },
  { Icon: HeartHandshake, title: "Accountability", body: "Small groups that actually show up for each other." },
];

export default function InnerCircle() {
  return (
    <div className="min-h-screen bg-white text-[#333333]" data-testid="inner-circle-page">
      <BrandHeader variant="cream" />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG} alt="Peaceful group gathering" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-tr from-[#333333]/70 via-[#333333]/40 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-24 pb-28 md:pt-36 md:pb-40">
          <div className="max-w-2xl text-white">
            <p className="text-xs font-bold tracking-[0.28em] uppercase text-[#F6C453] mb-5 flex items-center gap-2"><Users className="h-3.5 w-3.5" /> Coming Soon</p>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-[4rem] font-medium tracking-tight leading-[1.05]">
              The Wishing Wellness <span className="italic">Inner Circle.</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl opacity-90 leading-relaxed max-w-xl">
              A private membership for people committed to becoming their healthiest self — mind, body, and spirit. Members-only tools, live gatherings, and a community that actually shows up.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {perks.map(({ Icon, title, body }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.55, delay: (i % 4) * 0.05, ease: EASE }}
                className="bg-[#F8F6F2] rounded-3xl p-7 border border-[#EDE9E1]"
                data-testid={`perk-${i + 1}`}
              >
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#8FAF8A]/12 text-[#6E8F6A]">
                  <Icon className="h-5.5 w-5.5" strokeWidth={1.75} />
                </span>
                <h3 className="mt-5 font-display font-bold text-lg text-[#333333]">{title}</h3>
                <p className="mt-2 text-[#4A4A4A] text-sm leading-relaxed">{body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-[#F8F6F2]">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#8FAF8A] mb-4">Join the Waitlist</p>
          <h2 className="font-serif-display text-3xl md:text-4xl font-medium tracking-tight text-[#333333]">
            Be first through the door.
          </h2>
          <p className="mt-4 text-[#4A4A4A]">Founding-member pricing, first invitations, and a real say in what we build.</p>
          <div className="mt-8 max-w-2xl mx-auto text-left">
            <EmailSignup source="inner_circle" testIdPrefix="join" overline={null} headline={null} subtext={null} compact />
          </div>
        </div>
      </section>

      <BrandFooter />
    </div>
  );
}
